'use client';

import { useState, useEffect } from 'react';
import { 
  Target, Settings, FileSpreadsheet, List, FileText, 
  ShieldCheck, Send, CheckCircle2, Bot, Megaphone,
  BarChart3, RefreshCcw, Smartphone, Users 
} from 'lucide-react';
import styles from './CampaignsModule.module.css';

export function CampaignsModule({ reverse = false }: { reverse?: boolean }) {
  // Estados da Máquina de Animação
  const [phase, setPhase] = useState<'config' | 'typing' | 'preview' | 'sending' | 'done'>('config');
  const [typedName, setTypedName] = useState('');
  const [progress, setProgress] = useState(0);

  const campaignName = "Promoção Black Friday 50%";

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (phase === 'config') {
      setTypedName('');
      setProgress(0);
      timeout = setTimeout(() => setPhase('typing'), 1000);
    } 
    else if (phase === 'typing') {
      let i = 0;
      const typeInterval = setInterval(() => {
        setTypedName(campaignName.substring(0, i + 1));
        i++;
        if (i === campaignName.length) {
          clearInterval(typeInterval);
          setTimeout(() => setPhase('preview'), 800); // Pausa antes de mudar a fase
        }
      }, 50); // Velocidade da digitação
      return () => clearInterval(typeInterval);
    } 
    else if (phase === 'preview') {
      timeout = setTimeout(() => setPhase('sending'), 2000); // Fica 2s mostrando o celular e opções
    } 
    else if (phase === 'sending') {
      let p = 0;
      const progressInterval = setInterval(() => {
        p += Math.floor(Math.random() * 8) + 2; // Incremento randômico para parecer real
        if (p >= 100) {
          p = 100;
          clearInterval(progressInterval);
          setTimeout(() => setPhase('done'), 500);
        }
        setProgress(p);
      }, 150); // Velocidade da barra enchendo
      return () => clearInterval(progressInterval);
    } 
    else if (phase === 'done') {
      timeout = setTimeout(() => setPhase('config'), 3000); // Fica 3s no sucesso e reseta
    }

    return () => clearTimeout(timeout);
  }, [phase]);

  // Booleans derivados do estado para ativar as animações visuais
  const isTypingDone = phase === 'preview' || phase === 'sending' || phase === 'done';
  const showPreview = isTypingDone;
  const isClicked = phase === 'sending' || phase === 'done';

  return (
    <div className={styles.moduleWrapper} style={{ flexDirection: reverse ? 'row-reverse' : 'row' }}>
      
      {/* ==============================================
          COLUNA ESQUERDA: TEXTO E ARGUMENTOS
          ============================================== */}
      <div className={styles.textContent}>
        <span className={styles.badge}>Disparos em Massa</span>
        <h3 className={styles.title}>
          Sua Máquina de Lançamentos Automática
        </h3>
        <p className={styles.description}>
          Importe seus contatos, crie a mensagem e deixe o nosso motor inteligente enviar milhares de mensagens simulando o comportamento humano, protegendo seus chips de banimentos.
        </p>

        <div className={styles.featureList}>
          <div className={styles.featureItem}>
            <div className={styles.featureIcon}><Smartphone size={16} /></div>
            Integração nativa com WhatsApp, SMS e API Oficial.
          </div>
          <div className={styles.featureItem}>
            <div className={styles.featureIcon}><RefreshCcw size={16} /></div>
            Motor Anti-Ban: Rotação de instâncias e variáveis de texto.
          </div>
          <div className={styles.featureItem}>
            <div className={styles.featureIcon}><BarChart3 size={16} /></div>
            Acompanhamento de entrega, leitura e falhas em tempo real.
          </div>
        </div>
      </div>

      {/* ==============================================
          COLUNA DIREITA: MOCKUP ANIMADO
          ============================================== */}
      <div className={styles.mockupContainer}>
        <div className={styles.appWindow}>
          
          {/* HEADER DO APP */}
          <div className={styles.appHeader}>
            <div className={styles.headerIcon}><Megaphone size={20} /></div>
            <div>
              <span className={styles.headerTitle}>Nova Campanha</span>
              <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b' }}>Configure seu envio em massa</span>
            </div>
          </div>

          <div className={styles.appBody}>
            
            {/* LADO FORMULÁRIO */}
            <div className={styles.formSection}>
              
              {/* Box 1: Configurações Gerais */}
              <div className={styles.panelCard}>
                <div className={styles.panelTitle}><Settings size={14} color="#ea580c" /> Configurações Gerais</div>
                <div className={styles.inputGroup}>
                  <label>Nome da Campanha</label>
                  <div className={styles.inputField}>
                    {typedName}
                    {phase === 'typing' && <span className={styles.cursor}></span>}
                  </div>
                </div>
              </div>

              {/* Box 2: Base de Contatos */}
              <div className={styles.panelCard}>
                <div className={styles.panelTitle}><Users size={14} color="#ea580c" /> Base de Contatos</div>
                <div className={styles.optionsGrid}>
                  <div className={`${styles.optionBox} ${isTypingDone ? styles.active : ''}`}>
                    <FileSpreadsheet size={20} color={isTypingDone ? "#ea580c" : "#64748b"} />
                    <span>Planilha CSV</span>
                    <small>Importação em massa</small>
                  </div>
                  <div className={styles.optionBox}>
                    <List size={20} color="#64748b" />
                    <span>Lista Salva</span>
                    <small>Contatos do CRM</small>
                  </div>
                </div>
              </div>

              {/* Box 3: Controle Anti-Ban */}
              <div className={styles.panelCard}>
                <div className={styles.panelTitle}><ShieldCheck size={14} color="#10b981" /> Controle Anti-Ban</div>
                <div className={styles.speedControl}>
                  <div className={styles.speedItem}>Rápido</div>
                  <div className={`${styles.speedItem} ${isTypingDone ? styles.active : ''}`}>Médio (Seguro)</div>
                  <div className={styles.speedItem}>Automático</div>
                </div>
              </div>

              {/* Botão de Enviar */}
              <div className={`${styles.dispatchBtn} ${isClicked ? styles.clicked : ''}`}>
                <Send size={16} /> Disparar Campanha
              </div>

            </div>

            {/* LADO CELULAR PREVIEW */}
            <div className={styles.phoneSimulator}>
              <div className={styles.phoneHeader}>
                <div className={styles.phoneAvatar}><Bot size={14} /></div>
                <div>
                  <span className={styles.phoneTitle}>Aesir Bot</span>
                  <span className={styles.phoneStatus}>Online</span>
                </div>
              </div>
              <div className={styles.phoneChat}>
                {showPreview && (
                  <div className={styles.chatBubble}>
                    Olá, *João*! A nossa Black Friday começou com 50% de desconto. 🚀<br/><br/>
                    Acesse o link abaixo para garantir.
                  </div>
                )}
              </div>
              <div className={styles.phoneFooter}>
                <div className={styles.phoneInput}></div>
                <div className={styles.phoneMic}></div>
              </div>
            </div>

            {/* ==============================================
                OVERLAY DE PROGRESSO (SIMULAÇÃO DE ENVIO)
                ============================================== */}
            <div className={`${styles.progressOverlay} ${isClicked ? styles.active : ''}`}>
              
              {phase === 'sending' && (
                <div className={styles.progressCard}>
                  <div className={styles.progressHeader}>
                    <span className={styles.progressStatus}><Send size={18} color="#3b82f6" /> Enviando Disparos...</span>
                    <span className={styles.progressPct}>{progress}%</span>
                  </div>
                  <div className={styles.progressBarBg}>
                    <div className={styles.progressBarFill} style={{ width: `${progress}%` }}></div>
                  </div>
                  <div className={styles.progressMeta}>
                    <span>Base: 5.420 contatos</span>
                    <span>{Math.floor((5420 * progress) / 100)} enviados</span>
                  </div>
                </div>
              )}

              {phase === 'done' && (
                <div className={styles.progressCard} style={{ borderColor: '#10b981', background: '#ecfdf5' }}>
                  <CheckCircle2 size={48} color="#10b981" style={{ margin: '0 auto 16px auto' }} />
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#064e3b', margin: 0 }}>Disparo Concluído!</h3>
                  <p style={{ fontSize: '0.85rem', color: '#047857', marginTop: '8px' }}>Todas as 5.420 mensagens foram entregues com sucesso.</p>
                </div>
              )}

            </div>

          </div>
        </div>
      </div>

    </div>
  );
}